/**
 * Multi-Select Field Type - Common
 *
 * Field-type configuration and the shared lookup-API live binding used by both
 * the inline (inline/multiselect.tsx) and modal (modal/multiselect.tsx) renderers,
 * plus `MultiselectAutocomplete` — the table-specific Autocomplete editor those
 * renderers render while a multi-select cell is being edited.
 *
 * `getLookupApiFn` is an ES-module live binding: `initDependencies` calls the
 * setter, and both the inline and modal renderers (which import the binding from
 * this module) observe the updated value.
 */

import React, { useState, useCallback, useMemo, useRef, memo } from 'react';
import styled from '@emotion/styled';
import {
  type ChipTypeMap,
  type FilterOptionsState,
  type AutocompleteChangeDetails,
  type AutocompleteValue,
  type PaperProps,
  Box,
  Paper,
  createFilterOptions,
  Autocomplete as MuiAutocomplete,
  useTheme,
} from '@mui/material';
import { t } from '@lingui/macro';
import { debounce, omit } from 'lodash';
import { Check } from '@phosphor-icons/react/dist/ssr';
import { CaretDown, Trash, Warning } from '@phosphor-icons/react';
import { AutocompleteProps, ComboBoxOption } from 'components/inputs/types';
import { parseSearch } from 'components/inputs/ComboBox';
import { TextInput, TextInputProps } from 'components/inputs/TextInput';
import { Chip } from 'components/chip';
import { useGet } from 'lib/api';
import { colors } from 'styles/theme/colors';
import type { FieldTypeConfig } from '../../components/types';
import { L } from './columnWidths';

// ============================================================================
// Shared lookup-API live binding (set lazily to avoid circular dependencies)
// ============================================================================

// eslint-disable-next-line import/no-mutable-exports
export let getLookupApiFn: ((columnId: string) => unknown) | null = null;

/**
 * Set the getLookupApi function (called from initDependencies to avoid circular deps)
 */
export const setGetLookupApiForMultiselect = (fn: (columnId: string) => unknown): void => {
  getLookupApiFn = fn;
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Multi-select field type configuration
 */
export const multiselectFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...L,
};

// ============================================================================
// MultiselectAutocomplete - Autocomplete editor for multi-select table cells
// ============================================================================

/* eslint-disable @typescript-eslint/consistent-type-assertions --
 * The editor below wraps MUI's generic Autocomplete; the assertions are the
 * unavoidable generic plumbing (response transform -> Value[], AutocompleteValue,
 * filterOptions / omit -> TextInputProps, memo typing). No enable needed: the
 * component is the last thing in this file. */

/**
 * MultiselectAutocomplete - Autocomplete used by the inline/modal multi-select editors.
 *
 * A copy of the base Autocomplete with table-specific modifications:
 * 1. Adds `sortSelectedFirst` prop to sort options (selected first, then alphabetically)
 * 2. Removes the hardcoded onKeyDown that blocks Enter key propagation
 * 3. Adds `selectedValues` prop to track current selection for sorting
 */

/**
 * Additional props for MultiselectAutocomplete
 */
type MultiselectAutocompleteExtraProps<Value> = {
  /** When true, sorts options with selected items first, then alphabetically */
  sortSelectedFirst?: boolean;
  /** Currently selected values - used for sorting when sortSelectedFirst is true */
  selectedValues?: Value[];
  /** Optional "Clear" option to prepend when showClearOption is true (e.g. multiselect clear-all in dropdown) */
  clearOption?: Value;
  /** When true and clearOption is set and multiple, prepend clearOption to the options list */
  showClearOption?: boolean;
  /** Callback fired when the "Set empty" footer is clicked */
  onSetEmpty?: () => void;
};

const MultiselectAutocompleteInner = <
  Value extends Omit<ComboBoxOption, 'handleClick'>,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>({
  sx,
  options = [],
  openText = t`Open`,
  loadingText = t`Loading...`,
  noOptionsText = t`No options`,
  variant = 'unstyled',
  api = null,
  optionsLimit,
  atSearch = false,
  filterOptions: filterOptionsProps,
  onChange,
  onBlur,
  slotProps,
  optionsChipRender,
  optionsChipProps,
  isInvalidOption,
  sortSelectedFirst = false,
  selectedValues,
  clearOption,
  showClearOption = false,
  onSetEmpty,
  allowEmptySearchFetch = false,
  ...rest
}: Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  'inputVariant' | 'options'
> &
  MultiselectAutocompleteExtraProps<Value> & {
    options?: Value[];
    name?: string;
    required?: boolean;
    variant?: AutocompleteProps<
      Value,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >['inputVariant'];
  }) => {
  const [search, setSearch] = useState('');
  const theme = useTheme();

  // Helper to resolve vars (can be array or function)
  const getVars = useCallback(() => {
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  }, [api?.vars]);

  const { data } = useGet(
    api?.url || null,
    api?.search ? [api?.key, search] : api?.key || '',
    api?.search ? [parseSearch(atSearch, search), ...getVars()] : [...getVars()],
    undefined,
    api?.revalidate !== false,
    {
      enabled: (() => {
        if (!api) return false;
        if (api.search) {
          if (allowEmptySearchFetch) {
            return typeof api.vars !== 'function' || getVars().length > 0;
          }
          return Boolean(parseSearch(atSearch, search));
        }
        return true;
      })(),
      ...(api?.options ?? {}),
    }
  );
  const selectOptions = (data && api ? api.responseTransform(data) : options) as Value[];

  // Sort options: selected first, then alphabetically
  const sortedSelectOptions = useMemo(() => {
    if (!sortSelectedFirst || !selectedValues?.length) return selectOptions;
    const selectedIds = new Set(selectedValues.map(v => String(v.id)));
    return [...selectOptions].sort((a, b) => {
      const aSelected = selectedIds.has(String(a.id));
      const bSelected = selectedIds.has(String(b.id));
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (a.label ?? '').localeCompare(b.label ?? '');
    });
  }, [selectOptions, selectedValues, sortSelectedFirst]);

  // The footer's visibility, label and callback are read from refs so the PaperComponent
  // identity can stay stable across renders. Swapping PaperComponent (custom <-> default,
  // or a fresh function each render) remounts the popup and steals focus from the search
  // input — e.g. clicking "Set empty" emptied the selection, which flipped showClearOption
  // to false, hid the footer, and dropped focus mid-edit (DL-60135). A stable component
  // re-renders the popup in place and keeps focus until the user clicks away.
  const onSetEmptyRef = useRef(onSetEmpty);
  onSetEmptyRef.current = onSetEmpty;
  const showClearOptionRef = useRef(showClearOption);
  showClearOptionRef.current = showClearOption;
  const clearOptionLabelRef = useRef(clearOption?.label);
  clearOptionLabelRef.current = clearOption?.label;

  // Only the presence of clearOption (stable across an edit session) gates whether we own
  // the Paper at all, so consumers that never pass clearOption keep the default Paper.
  const hasClearOption = !!clearOption;
  const SetEmptyPaper = useMemo(() => {
    if (!hasClearOption) return undefined;
    return function SetEmptyPaperComponent(paperProps: PaperProps) {
      return (
        <Paper {...paperProps} elevation={8}>
          {paperProps.children}
          {showClearOptionRef.current && (
            <Box
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                onSetEmptyRef.current?.();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderTop: '0.5px solid',
                borderColor: 'border.primarySubtle',
                color: 'text.error',
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Trash size={20} color={colors.error[400]} />
              <span>{clearOptionLabelRef.current}</span>
            </Box>
          )}
        </Paper>
      );
    };
  }, [hasClearOption]);
  // Use useRef to create a stable debounced function that persists across renders
  const debouncedSetSearchRef = useRef(
    debounce((newInputValue: string) => {
      setSearch(newInputValue);
    }, 250)
  );

  // Stable callback that uses the ref
  const inputChangeApi = useCallback((newInputValue: string) => {
    debouncedSetSearchRef.current(newInputValue);
  }, []);

  // limit amount of options shown
  const defaultFilterOptions = createFilterOptions<Value>();
  const filterOptions = (opts: Value[], state: FilterOptionsState<Value>) => {
    if (atSearch) {
      const searchValue = parseSearch(atSearch, state.inputValue);
      if (!searchValue) return [];
      return defaultFilterOptions(opts, { ...state, inputValue: searchValue }).slice(
        0,
        optionsLimit
      );
    }
    return defaultFilterOptions(opts, state).slice(0, optionsLimit);
  };

  // The incoming `paper` slot prop may be an object or (per MUI's slotProps typing)
  // a callback; we only ever merge object-shaped overrides, so narrow to that here.
  const paperSlotProps =
    slotProps?.paper && typeof slotProps.paper === 'object'
      ? (slotProps.paper as { sx?: Record<string, unknown> })
      : undefined;

  return (
    <MuiAutocomplete
      selectOnFocus
      openText={openText}
      loadingText={loadingText}
      {...(SetEmptyPaper ? { slots: { ...rest.slots, paper: SetEmptyPaper } } : {})}
      // NOTE: No hardcoded onKeyDown here — the inline editors own the Enter key
      // themselves (e.g. EditableMultiselectCell's capture-phase handler).
      noOptionsText={noOptionsText}
      options={(sortedSelectOptions ?? []) as Value[]}
      sx={{
        '.MuiInputBase-root': {
          padding: 0,
          input: { padding: '10px 14px' },
        },
        '.MuiFormControl-root': {
          '.MuiInputBase-root': {
            input: { padding: '10px 14px' },
          },
        },
        '.MuiAutocomplete-endAdornment': {
          gap: '6px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          top: '50% !important',
        },
        flex: 1,
        ...sx,
      }}
      filterOptions={
        optionsLimit
          ? (filterOptions as (
              filteredOptions: Value[],
              state: FilterOptionsState<Value>
            ) => Value[])
          : filterOptionsProps
      }
      onInputChange={(event, newInputValue, reason) => {
        rest?.onInputChange?.(event, newInputValue, reason);
        if (api?.search) inputChangeApi(newInputValue);
      }}
      renderOption={(optionProps, option) => {
        const isInvalid = isInvalidOption?.(option);
        const textColor = isInvalid ? theme.palette.text.disabled : theme.palette.text.secondary;
        return (
          <OptionItem
            {...optionProps}
            $textColor={textColor}
            $isInvalid={!!isInvalid}
            key={option?.id}
          >
            {isInvalid && (
              <Warning color="inherit" size={24} style={{ minWidth: '24px', minHeight: '24px' }} />
            )}
            {option.CustomElement ? (
              <option.CustomElement label={option.label} {...option.customProps} />
            ) : (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.label}
              </span>
            )}
            {(optionsChipRender || optionsChipProps?.label) && (
              <Chip
                isTooltip={false}
                size="sm"
                label={optionsChipRender?.(option)}
                {...omit(optionsChipProps, ['labelProps'])}
                sx={themeSX => {
                  return {
                    marginLeft: 'auto',
                    ...optionsChipProps?.sxWithOption?.(themeSX, option),
                  };
                }}
              />
            )}
            {optionProps?.['aria-selected'] && (
              <Check
                size={20}
                style={{
                  minWidth: '20px',
                  minHeight: '20px',
                  ...(!(optionsChipRender || optionsChipProps?.label)
                    ? { marginLeft: 'auto' }
                    : {}),
                }}
                color={colors.brand[400]}
              />
            )}
          </OptionItem>
        );
      }}
      popupIcon={<CaretDown size={20} />}
      {...rest}
      slotProps={{
        ...slotProps,
        paper: {
          ...(paperSlotProps ?? {}),
          sx: {
            ...((sortedSelectOptions?.length > 0 || !rest?.freeSolo) && {
              marginTop: '8px',
              minWidth: 250,
              borderRadius: '8px',
              border: `0.5px solid ${colors.grey[200]}`,
              boxShadow:
                '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
              ul: {
                padding: '6px',
              },
            }),
            ...(paperSlotProps?.sx ?? {}),
          },
        },
      }}
      onChange={(event, value, reason, details) => {
        onChange?.(
          event,
          (value ?? '') as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
          reason,
          details as AutocompleteChangeDetails<Value>
        );
      }}
      onBlur={event => {
        onBlur?.(event);
      }}
      renderInput={params => {
        const { input, htmlInput } = params.slotProps;
        return (
          <TextInput
            {...(omit(rest, [
              'value',
              'name',
              'onBlur',
              'onChange',
              'data-testid',
            ]) as TextInputProps)}
            id={params.id}
            disabled={params.disabled}
            fullWidth={params.fullWidth}
            size={params.size}
            variant={variant}
            InputProps={{
              ...input,
              ...(rest.InputProps ?? {}),
              inputProps: htmlInput,
              startAdornment: input?.startAdornment ?? rest.InputProps?.startAdornment ?? (
                <EmptyStartAdornment aria-hidden />
              ),
            }}
          />
        );
      }}
    />
  );
};

// Wrap with memo for performance optimization
export const MultiselectAutocomplete = memo(
  MultiselectAutocompleteInner
) as typeof MultiselectAutocompleteInner;

// Zero-footprint placeholder used to keep the input's startAdornment slot occupied when
// the selection is empty (see renderInput above). `display: none` so it adds no flex gap
// or visual artefact, while still counting as a present startAdornment for MUI — which is
// what stops the input from being re-created (and blurred) when the last value is removed.
const EmptyStartAdornment = styled.span`
  display: none;
`;

// Option row for the dropdown. Native <li> (not MUI Box) styled via styled-components,
// per the frontend style guide; the colour applies without needing `!important`.
const OptionItem = styled.li<{ $textColor: string; $isInvalid: boolean }>`
  display: flex;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-direction: row;
  padding: 8px 10px;
  gap: 8px;
  margin-block: 1px;
  border-radius: 4px;
  color: ${p => p.$textColor};
  fill: ${p => p.$textColor};

  & span,
  & div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${p => (p.$isInvalid ? '& .MuiAvatar-root { opacity: 0.5; }' : '')}

  &.MuiAutocomplete-option[aria-selected='true'] {
    background: ${colors.brand[50]};
    font-weight: 600;

    & span,
    & div {
      font-weight: 600;
    }
  }
`;
